import { Inject, Injectable } from '@angular/core';
import { BehaviorSubject, Observable, of } from 'rxjs';
import { catchError, map, tap } from 'rxjs/operators';
import { omit } from 'lodash-es';

import type {
  ExtendedApiResponse,
  AccountPlanDto,
  AccountStatsRequest,
  AccountStatsResponse,
} from '@coin-depo-types';

import { StorageService } from '@coin-depo-core/storage';

import { ApiService } from '@coin-depo/http';
import { CurrencyService } from '@coin-depo-core/currency.service';
import { AccountStatsFacade } from '@coin-depo-core/account-stats';
import { AUTH_SERVICE_TOKEN, AuthFacade } from '@coin-depo-core/auth';

@Injectable({
  providedIn: 'root',
})
export class AccountStatsService implements AccountStatsFacade {
  private accountStats$ = new BehaviorSubject<AccountStatsResponse>(
    this.storage.get('accountStats', null)
  );
  cachedAccountStats$ = this.accountStats$.asObservable();
  private totalInterestPaid$ = new BehaviorSubject<number>(
    this.storage.get('totalInterestPaid', 0)
  );
  cachedTotalInterestPaid$ = this.totalInterestPaid$.asObservable();
  private totalAccruedInterest$ = new BehaviorSubject<number>(
    this.storage.get('totalAccruedInterest', 0)
  );
  cachedTotalAccruedInterest$ = this.totalAccruedInterest$.asObservable();
  private totalAvailableBalance$ = new BehaviorSubject<number>(
    this.storage.get('accountStats.total_available_balance', 0)
  );
  cachedTotalAvailableBalance$ = this.totalAvailableBalance$.asObservable();

  private totalBalance$ = new BehaviorSubject<number>(
    this.storage.get('accountStats.total_balance', 0)
  );
  cachedTotalBalance$ = this.totalBalance$.asObservable();

  constructor(
    @Inject(AUTH_SERVICE_TOKEN) private auth: AuthFacade,
    private balancesApi: ApiService,
    private storage: StorageService,
    private currencyService: CurrencyService,
  ) {}

  internalTransfer(
    from_account_id: number,
    to_account_id: number,
    amount: number,
    coin_id: number,
    comments = 'Transfer Between Accounts'
  ) {
    const userData = this.auth.userData;
    const data: any = {
      client_token: this.auth.accessToken,
      username: userData.email,
      from_account_id,
      to_account_id,
      amount,
      coin_id,
      comments,
    };
    return this.balancesApi.internalTransfer(data);
  }

  createDepositPlan(plan_id: number, coin_id: number, amount: number) {
    const userData = this.auth.userData;
    const data: any = {
      client_token: this.auth.accessToken,
      username: userData.email,
      plan_id,
      coin_id,
      amount,
    };
    return this.balancesApi.createDepositPlan(data);
  }

  withdrawFundsRequest(
    account_id: number,
    amount: number,
    address: string,
    coin: string,
    request_id: string,
    tag: string,
    estimate = false
  ) {
    const userData = this.auth.userData;
    const data: any = {
      client_token: this.auth.accessToken,
      username: userData.email,
      coin,
      account_id,
      amount,
      address,
      request_id,
      tag,
      estimate,
    };
    return this.balancesApi.withdrawFundsRequest(data);
  }

  withdrawFundsConfirmation(
    request_code: string,
    request_id: string,
    email_token: string,
    totp: string
  ) {
    const userData = this.auth.userData;
    const data: any = {
      client_token: this.auth.accessToken,
      username: userData.email,
      request_code,
      request_id,
      email_token,
      totp,
    };
    return this.balancesApi.withdrawFundsConfirmation(data);
  }

  resendWithdrawalCode(request_code: string) {
    const userData = this.auth.userData;
    const data: any = {
      client_token: this.auth.accessToken,
      username: userData.email,
      request_code,
    };
    return this.balancesApi.resendWithdrawalCode(data);
  }

  updateAccountStats(
    currency = this.currencyService.currentCurrency,
    waiteUntil?: Observable<any>
  ): Observable<AccountStatsResponse | string | null> {
    const userData = this.auth.userData;
    const data: AccountStatsRequest = {
      parent_id: this.auth.parentId!,
      client_token: this.auth.accessToken,
      username: userData.email,
      currency,
    };

    return this.balancesApi.getAccountStats(data).pipe(
      map((res: ExtendedApiResponse<AccountStatsResponse>) =>
        res.status === 'success'
          ? omit(res, ['parent_id', 'status', 'msg'])
          : res.msg
      ),
      map((res: AccountStatsResponse | string) => {
        if (typeof res === 'string') return res;
        //  map plans with asset to correct interest_coin_id
        res.plans = res.plans.map(plan =>
          (plan.interest_coin_id === 0 || plan.interest_coin_id === null) &&
          plan.binance_ticker !== 'COINDEPO'
            ? { ...plan, interest_coin_id: plan.coin_id }
            : plan
        );
      
        return res;
      }),
      // Network failure (status 0) yields HttpErrorResponse whose `error` is a
      // ProgressEvent, so `err.error.msg` is undefined. Emit a neutral `null`
      // signal instead — keeps combineLatest in navbar/portfolio-balance firing
      // (currency switch completes) while skipping setAccountStates. See
      // SENTRY-ANGULAR-REMEDIATION-PLAN Fix C2.
      catchError(() => of(null)),
      tap((res: AccountStatsResponse | string | null) => {
        if (res && typeof res !== 'string') {
          if (waiteUntil) {
            waiteUntil.subscribe(() => {
              this.setAccountStates(res);
            });
          } else {
            this.setAccountStates(res);
          }
        }
      })
    );
  }

  setPayoutCurrency(account_id: number, interest_coin_id: number) {
    const userData = this.auth.userData;
    const data: any = {
      client_token: this.auth.accessToken,
      username: userData.email,
      account_id,
      interest_coin_id
    };

    return this.balancesApi.setPayoutCurrency(data).pipe(
      tap(({ data, code }) => {
        if (code === 'PAYOUT_CURRENCY_UPDATED') {
          const currentPlans = this.getAccountPlansSnapshot();

          const newPlans = currentPlans.map(plan =>
            plan.user_account_id === data.account_id
              ? { ...plan, interest_coin_id: data.payout_coin_id }
              : plan
          );
          
          const newAccountStats = {
            ...this.getAccountStateSnapshot(),
            plans: newPlans
          };
          
          this.accountStats$.next(newAccountStats);
          this.storage.set('accountStats', newAccountStats);
        }
      })
    )
  }

  getAccountStateSnapshot(): AccountStatsResponse {
    return this.accountStats$.value;
  }

  getAccountPlansSnapshot(): AccountPlanDto[] {
    return this.getAccountStateSnapshot().plans;
  }

  getAccountPlanByIdSnapshot(coinId: string | number): AccountPlanDto[] | undefined {
    return this.getAccountPlansSnapshot().filter(
      (plan) => plan.coin_id === coinId
    );
  }

  setAccountVisibility(deposit_plan_id: number, visibility: boolean) {
    const userData = this.auth.userData;

    const data: any = {
      client_token: this.auth.accessToken,
      username: userData.email,
      visible: visibility ? 1 : 0,
      wallet_name: '0',
      deposit_plan_id,
    };

    return this.balancesApi.setAccountVisibility(data);
  }

  getUserWallet(ticker: string) {
    const data: any = {
      client_token: this.auth.accessToken,
      username: this.auth.userData.username,
      coin: ticker,
    };
    return this.balancesApi.getUserWallet(data);
  }

  private setAccountStates(accountStates: AccountStatsResponse) {
    if (!accountStates) { return; }
    this.accountStats$.next(accountStates);
    this.storage.set('accountStats', accountStates);
    this.setTotalInterestPaid(accountStates.total_interest_paid_currency);
    this.setTotalAvailableBalance(accountStates.total_available_balance);
    this.setTotalAccruedInterest(accountStates.total_accrued_interest_currency);
    this.setTotalBalance(accountStates.total_balance)
  }

  private setTotalAccruedInterest(totalAccruedInterest: number) {
    this.totalAccruedInterest$.next(totalAccruedInterest);
    this.storage.set('totalAccruedInterest', totalAccruedInterest);
  }

  private setTotalInterestPaid(totalInterestPaid: number) {
    this.totalInterestPaid$.next(totalInterestPaid);
    this.storage.set('totalInterestPaid', totalInterestPaid);
  }

  private setTotalAvailableBalance(total_available_balance: number) {
    this.totalAvailableBalance$.next(total_available_balance);
    this.storage.set(
      'accountStats.total_available_balance',
      total_available_balance
    );
  }

  private setTotalBalance(total_balance: number) {
    this.totalBalance$.next(total_balance);
    this.storage.set(
      'accountStats.total_balance',
      total_balance
    );
  }
}
